import { z } from 'zod';

/**
 * Status-Maschine des Kommissionierauftrags (ADR 0036, erweitert ADR 0043).
 * KOMMISSIONIERT trennt "Kommissionierung abschließen" (Lagerist) vom
 * nachgelagerten "Lagerausgang buchen" (Admin/Sekretariat).
 */
export const pickingStatusSchema = z.enum([
  'ENTWURF',
  'FREIGEGEBEN',
  'IN_ARBEIT',
  'KOMMISSIONIERT',
  'ABGESCHLOSSEN',
  'STORNIERT',
]);
export type PickingStatus = z.infer<typeof pickingStatusSchema>;

/** Kommissionierauftrag anlegen — Positionen werden aus der Projekt-Materialliste übernommen (Snapshot). */
export const createPickingOrderSchema = z.object({
  projectId: z.string().uuid(),
  notes: z.string().trim().max(2000).optional(),
});
export type CreatePickingOrderInput = z.infer<typeof createPickingOrderSchema>;

/** Büro-/Projektleiter-Vermerk je Position (z. B. „KW34", „kürzen 20 mm") + optionaler Termin. */
export const updatePickingItemSchema = z.object({
  instructionNote: z.string().trim().max(2000).nullable().optional(),
  dueDate: z.string().datetime().nullable().optional(),
});
export type UpdatePickingItemInput = z.infer<typeof updatePickingItemSchema>;

/**
 * Lageristen-Rückmeldung je Position (P2): kommissionierte Ist-Menge, Lager-Vermerk
 * und Packstück-Zuordnung (P3). Alle Felder optional — nur Übermitteltes wird gesetzt.
 */
export const pickPickingItemSchema = z
  .object({
    pickedQty: z.number().min(0).max(1_000_000_000),
    warehouseNote: z.string().trim().max(2000).nullable(),
    packageId: z.string().uuid().nullable(),
  })
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'Mindestens ein Feld erforderlich.' });
export type PickPickingItemInput = z.infer<typeof pickPickingItemSchema>;

/**
 * Mehrere Positionen in einem Rutsch zurückmelden (P2, kumuliertes Kommissionieren):
 * das Abhaken eines Artikels verteilt die Menge auf alle gleichen Positionen.
 */
export const bulkPickSchema = z.object({
  items: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        pickedQty: z.number().min(0).max(1_000_000_000).optional(),
        warehouseNote: z.string().trim().max(2000).nullable().optional(),
        packageId: z.string().uuid().nullable().optional(),
      }),
    )
    .min(1)
    .max(1000),
});
export type BulkPickInput = z.infer<typeof bulkPickSchema>;

/** Zusätzliche Position während der Kommissionierung anlegen (Lagerist ergänzt manuell). */
export const addPickingItemSchema = z.object({
  articleId: z.string().uuid().nullable().optional(),
  articleNumber: z.string().trim().min(1).max(120),
  description: z.string().trim().min(1).max(500),
  unit: z.string().trim().max(20).nullable().optional(),
  requiredQty: z.number().positive().max(1_000_000_000),
});
export type AddPickingItemInput = z.infer<typeof addPickingItemSchema>;

// Maß (mm) bzw. Gewicht (g) — Ganzzahl, keine Float-Rundungsfehler (ADR 0036).
const measure = z.number().int().min(0).max(100_000_000).nullable().optional();

/** Packstück/Colli anlegen (P3) — Maße in Millimeter, Gewichte in Gramm (Ganzzahl). */
export const createPickingPackageSchema = z.object({
  label: z.string().trim().min(1).max(120).optional(),
  /**
   * Frei vergebener Spitzname („Große Holzkiste", Tech-Lead 2026-07-27) — Verwechslungsschutz
   * beim Packen. Ergänzt `label`; die Colli-Nummer bleibt der stabile Bezug für Beleg/Packliste.
   */
  nickname: z.string().trim().max(80).nullable().optional(),
  lengthMm: measure,
  widthMm: measure,
  heightMm: measure,
  grossWeightG: measure,
  tareWeightG: measure,
  note: z.string().trim().max(2000).nullable().optional(),
});
export type CreatePickingPackageInput = z.infer<typeof createPickingPackageSchema>;

/** Packstück ändern (P3) — gleiche Felder, alle optional. */
export const updatePickingPackageSchema = createPickingPackageSchema.partial();
export type UpdatePickingPackageInput = z.infer<typeof updatePickingPackageSchema>;

/** Foto-Metadaten beim Upload (P3.5) — Datei kommt als multipart, hier die Zuordnung. */
export const uploadPickingPhotoSchema = z.object({
  packageId: z.string().uuid().nullable().optional(),
  pickingItemId: z.string().uuid().nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type UploadPickingPhotoInput = z.infer<typeof uploadPickingPhotoSchema>;

/**
 * Eine offene Bestellung, die eine Position beliefert (statt Lagerentnahme, ADR 0060):
 * Lieferant, Bestellnummer und voraussichtlicher Liefertermin. Leeres Array = Position
 * kommt aus dem Lager (oder es wurde noch keine Bestellung ausgelöst).
 */
export const pickingSourcingLineSchema = z.object({
  supplierName: z.string(),
  orderNumber: z.string(),
  expectedDate: z.string().datetime().nullable(),
});
export type PickingSourcingLine = z.infer<typeof pickingSourcingLineSchema>;

/**
 * Aufteilung einer Position auf mehrere Packstücke (Tech-Lead 2026-07-22): manche
 * Positionen werden physisch auf mehrere Collis verteilt (z. B. 60 Stk in Colli 1, 40
 * Stk in Colli 2). Die Position bleibt dabei EINE Zeile/POS-Nr. — nur die gepickte
 * Menge wird je Packstück aufgeteilt. Leeres Array (Regelfall) = keine Aufteilung,
 * `PickingItem.packageId` bleibt maßgeblich für die einfache Ein-Packstück-Zuordnung.
 */
export const pickingItemPackageSplitSchema = z.object({
  packageId: z.string().uuid(),
  qty: z.number().positive().max(1_000_000_000),
});
export type PickingItemPackageSplit = z.infer<typeof pickingItemPackageSplitSchema>;

/** Aufteilung einer Position setzen — ersetzt alle bisherigen Splits dieser Position komplett. */
export const setPickingItemPackageSplitsSchema = z.object({
  splits: z
    .array(pickingItemPackageSplitSchema)
    .max(50)
    .refine((splits) => new Set(splits.map((split) => split.packageId)).size === splits.length, {
      message: 'Jedes Packstück darf in der Aufteilung nur einmal vorkommen.',
    }),
});
export type SetPickingItemPackageSplitsInput = z.infer<typeof setPickingItemPackageSplitsSchema>;

/**
 * Aufteilung mehrerer Positionen in EINEM Aufruf setzen (kumulierter Modus, ADR 0061
 * erweitert): eine kumulierte Gruppe wird auf mehrere Collis verteilt und der Client
 * bildet das auf die einzelnen Positionen ab (distributeGroupPackageSplits). Ein
 * Bulk-Call statt N Einzel-PATCHes — atomar (eine Transaktion), ein Audit-Eintrag,
 * ein optimistisches Cache-Update. Ersetzt je genannter Position die komplette Aufteilung.
 */
export const setPickingItemsPackageSplitsBulkSchema = z.object({
  items: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        splits: z
          .array(pickingItemPackageSplitSchema)
          .max(50)
          .refine((splits) => new Set(splits.map((split) => split.packageId)).size === splits.length, {
            message: 'Jedes Packstück darf in der Aufteilung einer Position nur einmal vorkommen.',
          }),
      }),
    )
    .min(1)
    .max(1000),
});
export type SetPickingItemsPackageSplitsBulkInput = z.infer<typeof setPickingItemsPackageSplitsBulkSchema>;

export const pickingItemResponseSchema = z.object({
  id: z.string().uuid(),
  pickingOrderId: z.string().uuid(),
  position: z.number().int(),
  sourceMaterialItemId: z.string().uuid().nullable(),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string(),
  description: z.string(),
  /** Snapshot der Baugruppe zum Zeitpunkt der Auftragserzeugung (ADR 0043). */
  assemblyGroup: z.string().nullable(),
  unit: z.string().nullable(),
  requiredQty: z.number(),
  pickedQty: z.number(),
  /** Büro/Projektleiter-Vermerk: „KW34", „kürzen 20 mm". */
  instructionNote: z.string().nullable(),
  dueDate: z.string().datetime().nullable(),
  /** Lageristen-Vermerk. */
  warehouseNote: z.string().nullable(),
  packageId: z.string().uuid().nullable(),
  /** Offene Bestellung(en) für diese Position — leer = aus dem Lager entnehmen (ADR 0060). */
  sourcing: z.array(pickingSourcingLineSchema),
  /** Aufteilung auf mehrere Packstücke — leer (Regelfall) = `packageId` ist maßgeblich. */
  packageSplits: z.array(pickingItemPackageSplitSchema),
});
export type PickingItemResponse = z.infer<typeof pickingItemResponseSchema>;

export const pickingPackageResponseSchema = z.object({
  id: z.string().uuid(),
  pickingOrderId: z.string().uuid(),
  label: z.string(),
  /** Frei vergebener Spitzname — null = keiner vergeben (Regelfall). */
  nickname: z.string().nullable(),
  /** Maße in Millimeter, Gewichte in Gramm (Ganzzahl). Netto = Brutto − Tara. */
  lengthMm: z.number().int().nullable(),
  widthMm: z.number().int().nullable(),
  heightMm: z.number().int().nullable(),
  grossWeightG: z.number().int().nullable(),
  tareWeightG: z.number().int().nullable(),
  note: z.string().nullable(),
});
export type PickingPackageResponse = z.infer<typeof pickingPackageResponseSchema>;

export const pickingPhotoResponseSchema = z.object({
  id: z.string().uuid(),
  pickingOrderId: z.string().uuid(),
  packageId: z.string().uuid().nullable(),
  pickingItemId: z.string().uuid().nullable(),
  fileName: z.string(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type PickingPhotoResponse = z.infer<typeof pickingPhotoResponseSchema>;

export const pickingOrderResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  projectId: z.string().uuid(),
  /** Projekt-Code + -Name (Join, für die Anzeige — niemand merkt sich die KO-Nummer). */
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  /** Liefertermin des Projekts (Join) — zur Priorisierung in der Kommissionierungs-Liste. */
  projectDeliveryDate: z.string().datetime().nullable(),
  number: z.string(),
  status: pickingStatusSchema,
  notes: z.string().nullable(),
  releasedBy: z.string().uuid().nullable(),
  releasedAt: z.string().datetime().nullable(),
  completedBy: z.string().uuid().nullable(),
  completedAt: z.string().datetime().nullable(),
  /** Verknüpfter Lieferschein (Verkauf), sobald gewandelt — sonst null. */
  deliveryNoteId: z.string().uuid().nullable(),
  items: z.array(pickingItemResponseSchema),
  packages: z.array(pickingPackageResponseSchema),
  photos: z.array(pickingPhotoResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PickingOrderResponse = z.infer<typeof pickingOrderResponseSchema>;

/** Listen-Zeile (ohne Positionen/Packstücke/Fotos) für die Übersicht. */
export const pickingOrderListItemSchema = pickingOrderResponseSchema.omit({
  items: true,
  packages: true,
  photos: true,
});
export type PickingOrderListItem = z.infer<typeof pickingOrderListItemSchema>;
